import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { DateField } from '../../../../components/inputs/DateField';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { budgetPlansApi } from '../budgetPlansApi';
import type { BudgetPlanDetail } from '../budgetPlanTypes';
import { sampleBudgetPlan } from '../utils/sampleBudgetPlan';

/**
 * Create or correct one budget plan — the fiscal year's HEADER, and nothing else.
 *
 * **Five fields**: the year, the submission deadline, the coverage period, a description and a
 * note. That is the reference's own create payload exactly, and it is the whole form — a plan
 * is created as a header, and the year's goals are set up afterwards on the plan's own Goal
 * Setting tab.
 *
 * **It used to collect the Goal Setting too**, which broke two rules at once: the profile's
 * Details card and its Goal Setting card both opened this one modal, so "one card, one modal"
 * failed from both ends; and the reference's plan form never collects a goal at all, so the
 * create process diverged from the source it was captured from. Both had one cause.
 */
export function BudgetPlanModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: BudgetPlanDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [fiscalYear, setFiscalYear] = useState(
    editing ? String(editing.plan.fiscalYear) : String(new Date().getFullYear() + 1),
  );
  const [submissionClosesAt, setSubmissionClosesAt] = useState(editing?.plan.submissionClosesAt ?? '');
  const [coverageStartDate, setCoverageStartDate] = useState(editing?.plan.coverageStartDate ?? '');
  const [coverageEndDate, setCoverageEndDate] = useState(editing?.plan.coverageEndDate ?? '');
  const [description, setDescription] = useState(editing?.plan.description ?? '');
  const [notes, setNotes] = useState(editing?.plan.notes ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const year = Number(fiscalYear);

  const valid =
    Number.isInteger(year) &&
    year >= 2000 &&
    year <= 2100 &&
    submissionClosesAt.length > 0 &&
    coverageStartDate.length > 0 &&
    coverageEndDate.length > 0;

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      fiscalYear: year,
      submissionClosesAt,
      coverageStartDate,
      coverageEndDate,
      description: description.trim() || null,
      notes: notes.trim() || null,
    };

    try {
      if (editing) await budgetPlansApi.update(editing.plan.id, request);
      else await budgetPlansApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the plan.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'budget-plan' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.plan.planNumber}` : 'New budget plan'}
      size="lg"
      id="budget-plan-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleBudgetPlan();
          setFiscalYear(String(sample.fiscalYear));
          setSubmissionClosesAt(sample.submissionClosesAt);
          setCoverageStartDate(sample.coverageStartDate);
          setCoverageEndDate(sample.coverageEndDate);
          setDescription(sample.description);
          setNotes(sample.notes);

          /*
            NO GOALS TO FILL. This seeded three from the live catalog, because a plan that had
            adopted none could not open its submission window — a generated record whose one
            lifecycle action was refused. There is no adoption now: the window opens against
            the catalog, so the generator has nothing left to answer here.

            Every required field on this form is filled above, which is the whole contract.
          */
        },
        disabled: saving,
      }}
      confirm={{
        id: 'budget-plan-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="budget-plan-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="budget-plan-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="budget-plan-fiscal-year"
            label="Fiscal year"
            required
            type="number"
            description="The year this plan covers. One active plan per year — a second is refused."
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            disabled={saving}
            error={error?.fieldError('fiscalYear')}
          />

          {/* A date takes ONE column, always — never a span. */}
          <DateField
            id="budget-plan-submission-closes-at"
            label="Submission deadline"
            required
            description="The day departments must have submitted by. It may fall well before the year begins — that is the ordinary case."
            value={submissionClosesAt}
            onChange={setSubmissionClosesAt}
            disabled={saving}
            error={error?.fieldError('submissionClosesAt')}
          />

          <DateField
            id="budget-plan-coverage-start-date"
            label="Coverage start"
            required
            description="First day of the fiscal period the plan covers."
            value={coverageStartDate}
            onChange={setCoverageStartDate}
            disabled={saving}
            error={error?.fieldError('coverageStartDate')}
          />

          <DateField
            id="budget-plan-coverage-end-date"
            label="Coverage end"
            required
            description="Last day of the period. Must fall on or after the start."
            value={coverageEndDate}
            onChange={setCoverageEndDate}
            disabled={saving}
            error={error?.fieldError('coverageEndDate')}
          />
        </div>

        {/*
          NO GOAL FIELD, and its removal is what makes this form the DETAILS card's own edit.

          It collected the year's whole Goal Setting, so one cog opened a modal holding the
          fiscal year, the window, the coverage, the goals and the description — and the Goal
          Setting card's cog opened the SAME one. Two cards, one modal, which is "one card, one
          modal" broken from both ends.

          It also diverged from the reference, whose plan form collects exactly five fields and
          never a goal: a plan is created as a HEADER, and the goals are set up afterwards on
          the plan's own tab. Both defects had one cause and one fix.
        */}
        {/*
          A free note about the record as a whole goes FULL WIDTH, outside the grid — the
          remarks rule. Half a row is the box that truncates while the field beside it needed
          none of the room.
        */}
        <Textarea
          id="budget-plan-description"
          label="Description"
          description="What this year's plan is for, in a sentence or two."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        <Textarea
          id="budget-plan-notes"
          label="Notes"
          description="Anything a reader of this plan should know that the fields above do not say."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />
      </div>
    </Modal>
  );
}
